import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Box, Tabs, Typography } from '@evaneos/b2b-ui';

import ForumIcon from '@evaneos/b2b-ui/icons/QuestionAnswerOutlined';
import LibraryIcon from '@evaneos/b2b-ui/icons/LibraryBooksOutlined';

import { PanelTab } from '../../../../../design-system/PanelTab';

import { PhoneCall } from '../../../../../resources/phoning/types';
import { DossierId } from '../../../../../resources/dossier/types';
import { useRetryTranscript, useRetrySummary } from '../../../../../resources/phoning/mutations';
import {
    useIsTranscriptGenerating,
    useIsSummaryGenerating,
    usePhoneCallDisplayState,
} from '../../../../../resources/phoning/hooks';
import { useTranscriptGeneration } from '../../../../../shared/providers/TranscriptGenerationProvider';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';
import { useSidePanel } from '../../../SidePanel/SidePanelContext';
import { useGetPhoneCalls } from '../../../../../resources/phoning/queries';
import { usePhoneCallFromUrl } from '../../hooks/usePhoneCallFromUrl';

import { ConfirmRegenerationModal } from '../ConfirmRegenerationModal';

import { AudioPlayer } from './AudioPlayer';
import { SummaryTab } from './SummaryTab';
import { TranscriptTab } from './TranscriptTab';
import { GenerateSummaryEmailBlock } from './GenerateSummaryEmailBlock';

type RecordedCallDetailsProps = {
    phoneCall: PhoneCall;
};

type TabType = 'summary' | 'transcript';
type RegenerationType = 'transcript' | 'summary';

export const RecordedCallDetails = ({ phoneCall: initialPhoneCall }: RecordedCallDetailsProps) => {
    const [activeTab, setActiveTab] = useState<TabType>('summary');
    const [regenerationModalType, setRegenerationModalType] = useState<RegenerationType | null>(
        null,
    );

    const { dossierId } = useParams() as { dossierId: DossierId };
    const { formatMessage, formatDate, formatTime } = useIntl();

    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { isOpen } = useSidePanel();

    const { mutate: retryTranscript } = useRetryTranscript();
    const { mutate: retrySummary } = useRetrySummary();
    const { startTranscriptGeneration, startSummaryGeneration } = useTranscriptGeneration();

    const { data: phoneCalls } = useGetPhoneCalls(dossierId);

    const { phoneCallIdToDisplay, cleanUpUrlParams } = usePhoneCallFromUrl(initialPhoneCall.id);

    const phoneCall =
        phoneCalls?.find((call) => call.id === phoneCallIdToDisplay) ?? initialPhoneCall;

    const displayState = usePhoneCallDisplayState(phoneCall);

    const isTranscriptGeneratingForCall = useIsTranscriptGenerating(phoneCall);
    const isSummaryGeneratingForCall = useIsSummaryGenerating(phoneCall);

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    const [prevPhoneCallId, setPrevPhoneCallId] = useState(initialPhoneCall.id);

    if (prevIsOpen !== isOpen && !isOpen) {
        setPrevIsOpen(isOpen);
        setActiveTab('summary');
    }

    if (prevPhoneCallId !== initialPhoneCall.id) {
        setPrevPhoneCallId(initialPhoneCall.id);
        setActiveTab('summary');
    }

    // Clean up URL params when panel closes (side effect — must stay in an effect)
    useEffect(() => {
        if (!isOpen) {
            cleanUpUrlParams();
        }
    }, [cleanUpUrlParams, isOpen]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: TabType) => {
        setActiveTab(newValue);

        const eventName =
            newValue === 'summary' ? 'summaryAiTranscriptViewed' : 'rawTranscriptOpened';
        trackEvent((ampli, defaultProperties) => {
            ampli[eventName]({
                ...defaultProperties,
                ...trackedTripProperties,
                call_id: phoneCall.id.toString(),
            });
        });
    };

    const handleRetryTranscript = () => {
        if (!phoneCall.dossier_phone_call_id) return;

        startTranscriptGeneration(phoneCall.id);
        retryTranscript({
            dossierId,
            dossierPhoneCallId: phoneCall.dossier_phone_call_id,
        });
    };

    const handleRetrySummary = () => {
        if (!phoneCall.dossier_phone_call_id) return;

        startSummaryGeneration(phoneCall.id);
        retrySummary({
            dossierId,
            dossierPhoneCallId: phoneCall.dossier_phone_call_id,
        });
    };

    const handleConfirmRegeneration = () => {
        if (regenerationModalType === 'transcript') {
            handleRetryTranscript();
        } else if (regenerationModalType === 'summary') {
            handleRetrySummary();
        }
    };

    const canRegenerate = !displayState.isAudioDeleted;
    const shouldShowTranscriptError = !displayState.hasSummary && displayState.hasTranscriptError;
    const canGenerateEmailRecap = !displayState.hasSummaryError && displayState.hasSummary;

    return (
        <Box
            component="section"
            sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}
        >
            <Box sx={{ mb: 3 }} component="header">
                <Typography
                    color="text.secondary"
                    sx={{ mb: 0.25, fontSize: '1.2rem', fontWeight: 'bold' }}
                >
                    {formatDate(phoneCall.sendDate, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    })}
                    ,{' '}
                    {formatTime(phoneCall.sendDate, {
                        hour: '2-digit',
                        minute: '2-digit',
                    })}{' '}
                </Typography>
                <Typography variant="h4" component="h2">
                    {formatMessage({ id: 'dossier.discussion.phone_call.voice_call_header' })}
                </Typography>
            </Box>

            <GenerateSummaryEmailBlock
                phoneCall={phoneCall}
                dossierId={dossierId}
                canGenerateEmailRecap={canGenerateEmailRecap}
            />

            {displayState.showAudio && (
                <AudioPlayer
                    audioUrl={phoneCall.audio_file}
                    isDeleted={displayState.isAudioDeleted}
                    phoneCallId={phoneCall.id}
                />
            )}

            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mt: 1.5,
                }}
            >
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <PanelTab
                        value="summary"
                        label={formatMessage({ id: 'dossier.discussion.phone_call.tab_summary' })}
                        icon={<ForumIcon fontSize="small" />}
                        iconPosition="start"
                    />
                    <PanelTab
                        value="transcript"
                        label={formatMessage({
                            id: 'dossier.discussion.phone_call.tab_raw_transcript',
                        })}
                        icon={<LibraryIcon fontSize="small" />}
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', paddingY: 1.5 }}>
                {activeTab === 'summary' && (
                    <SummaryTab
                        summary={phoneCall.summary}
                        hasSummaryError={displayState.hasSummaryError}
                        shouldShowTranscriptError={shouldShowTranscriptError}
                        canRegenerate={canRegenerate}
                        isRegenerating={isSummaryGeneratingForCall}
                        onRegenerate={() =>
                            setRegenerationModalType(
                                shouldShowTranscriptError ? 'transcript' : 'summary',
                            )
                        }
                    />
                )}
                {activeTab === 'transcript' && (
                    <TranscriptTab
                        transcript={phoneCall.raw_transcription}
                        isTranscriptDeleted={displayState.isTranscriptDeleted}
                        hasTranscriptError={displayState.hasTranscriptError}
                        canRegenerate={canRegenerate}
                        isRegenerating={isTranscriptGeneratingForCall}
                        onRegenerate={() => setRegenerationModalType('transcript')}
                    />
                )}
            </Box>

            {regenerationModalType && (
                <ConfirmRegenerationModal
                    open={true}
                    setOpen={(open) => !open && setRegenerationModalType(null)}
                    type={regenerationModalType}
                    handleConfirmation={handleConfirmRegeneration}
                    dossierId={dossierId}
                />
            )}
        </Box>
    );
};
